import JsBarcode from "jsbarcode";
import CompanyLogoForm from "react-lib/apps/HISV3/common/CompanyLogoForm";
import { formatComma } from "react-lib/utils/tsUtils";

const FORM_NAME = "FormSupplyReport";

// Types
type FormSupplyReportProps = {
  // patientData
  hn: string;
  patientName: string;
  encounter: string;
  coverage: string;
  birthdate: string;
  fullAge: string;
  startEncounter: string;
  divisionName: string;
  doctorName: string;
  // supplyDetail
  supplyCode: string;
  supplyType: string;
  supplyStatus: string;
  supplyOrderTime: string;
  supplyOrderDivision: string;
  supplyEditUser: string;
  supplyPerformDivision: string;
  // supplyData
  supplyItem: Record<string, any>[];
  priceSummary: {
    total: string;
    claimable: string;
    nonClaimable: string;
  };
};

const FormSupplyReport = async (props: FormSupplyReportProps) => {
  const companyLogoForm = await CompanyLogoForm({
    font: "THSarabunNew",
    height: 40,
    form: FORM_NAME,
  });
  const { font, images } = companyLogoForm;

  const convertTextToBase64BarcodeBlob = (text: string) => {
    const canvas = document.createElement("canvas");

    JsBarcode(canvas, text, { displayValue: false });

    return canvas.toDataURL("image/png");
  };

  const supplyDetail = (label: string, value: string) => ({
    text: [{ text: `${label}: `, bold: true }, { text: value }],
    fontSize: 11,
    lineHeight: 0.7,
    margin: [0, 1, 0, 1],
  });

  const patientDetail = (label: string, value: string) => ({
    text: [{ text: `${label}: `, bold: true }, { text: value }],
  });

  const supplyData = () => {
    return props.supplyItem.map((item) => {
      return [
        {
          text: item?.no || "",
          style: {
            alignment: "center",
          },
          border: [false, false, false, false],
        },
        {
          text: item?.code || "",
          style: {
            alignment: "center",
          },
          border: [false, false, false, false],
        },
        {
          text: item?.name || "",
          border: [false, false, false, false],
        },
        {
          text: item?.quantity_request || "",
          style: {
            alignment: "center",
          },
          border: [false, false, false, false],
        },
        {
          text: formatComma(item?.price_total) || "",
          style: {
            alignment: "center",
          },
          border: [false, false, false, false],
        },
        {
          text: formatComma(item?.price_claimable) || "",
          style: {
            alignment: "center",
          },
          border: [false, false, false, false],
        },
        {
          text: formatComma(item?.price_non_claimable) || "",
          style: {
            alignment: "center",
          },
          border: [false, false, false, false],
        },
      ];
    });
  };

  return {
    defaultStyle: {
      font: font,
      fontSize: 14,
    },
    pageSize: "A4",
    pageOrientation: "landscape",
    pageMargins: [5, 165, 5, 40],
    header: function (currentPage: any) {
      return {
        margin: [10, 13, 10, 0],
        stack: [
          {
            margin: [0, 0, 0, 0],
            table: {
              widths: [300, "*", 150, 150],
              body: [
                [
                  {
                    columns: [
                      {
                        image: "logo",
                        width: 120,
                        height: 36,
                        border: [false, false, false, false],
                        alignment: "left",
                        margin: [0, 0, 0, 0],
                      },
                      {
                        stack: [
                          {
                            text: "โรงพยาบาลคณะทันตแพทย์ศาสตร์",
                            style: {
                              bold: true,
                              fontSize: 10,
                            },
                          },
                          {
                            text: "Chulalongkorn University Faculty of Dentistry",
                            style: {
                              bold: true,
                              fontSize: 10,
                            },
                          },
                          {
                            text: "ถนนอังรีดูนังต์ ปทุมวัน กรุงเทพฯ 10330 โทร. 02-218-8705",
                            style: {
                              fontSize: 10,
                            },
                          },
                        ],
                      },
                    ],
                    border: [false, false, false, false],
                  },
                  {
                    text: `ใบสั่งจ่ายเวชภัณฑ์`,
                    style: {
                      bold: true,
                      fontSize: 22,
                      alignment: "center",
                    },
                    margin: [0, 10, 0, 0],
                    border: [false, false, false, false],
                  },
                  {
                    text: "",
                    border: [false, false, false, false],
                  },
                  {
                    stack: [
                      supplyDetail("เลขที่ใบสั่งจ่ายเวชภัณฑ์", props.supplyCode || ""),
                      {
                        image: convertTextToBase64BarcodeBlob(props.supplyCode || ""),
                        width: 120,
                        height: 25,
                        margin: [0, 3, 0, 0],
                      },
                      supplyDetail("ประเภท", props.supplyType || ""),
                      supplyDetail("สถานะ", props.supplyStatus || ""),
                      supplyDetail("วันที่สั่ง", props.supplyOrderTime || ""),
                      supplyDetail("หน่วยงานขอเบิก", props.supplyOrderDivision || ""),
                      supplyDetail("ผู้สั่ง", props.supplyEditUser || ""),
                    ],
                    rowSpan: 2,
                    border: [false, false, false, false],
                  },
                ],
                [
                  {
                    columns: [
                      {
                        width: 150,
                        stack: [
                          patientDetail("HN", props.hn || ""),
                          patientDetail("สิทธิ", props.coverage || ""),
                        ],
                        margin: [0, 0, 20, 0],
                      },
                      {
                        width: 260,
                        stack: [patientDetail("ชื่อผู้ป่วย", props.patientName || "")],
                        margin: [0, 0, 20, 0],
                      },
                      {
                        width: "*",
                        stack: [
                          patientDetail("Encounter", props.encounter || ""),
                          patientDetail("วันที่รับบริการ", props.startEncounter || ""),
                        ],
                      },
                    ],
                    colSpan: 3,
                    margin: [0, 30, 0, 0],
                    border: [false, false, false, false],
                  },
                  {},
                  {},
                  {},
                ],
                [
                  {
                    columns: [
                      {
                        width: 150,
                        stack: [
                          patientDetail("วัน/เดือน/ปีเกิด", props.birthdate || ""),
                          patientDetail("อายุ", props.fullAge || ""),
                        ],
                        margin: [0, 0, 20, 0],
                      },
                      {
                        width: 260,
                        stack: [],
                        margin: [0, 0, 20, 0],
                      },
                      {
                        width: "*",
                        stack: [
                          patientDetail("แผนก", props.divisionName || ""),
                          patientDetail("แพทย์", props.doctorName || ""),
                        ],
                      },
                    ],
                    colSpan: 4,
                    margin: [0, 0, 0, 0],
                    border: [false, false, false, true],
                  },
                  {},
                  {},
                  {},
                ],
              ],
            },
            layout: {
              hLineStyle: function (i: number, _node: any) {
                return { dash: { length: 2, space: 2 } };
              },
              hLineWidth: function (i: number, _node: any) {
                return 0.5;
              },
              paddingTop: () => 0,
              paddingBottom: () => 0,
            },
          },
        ],
      };
    },
    content: [
      {
        margin: [0, 0, 0, 0],
        table: {
          headerRows: 1,
          widths: [50, 80, "*", 60, 70, 70, 70],
          body: [
            [
              {
                text: "ลำดับ",
                style: {
                  alignment: "center",
                  bold: true,
                },
                margin: [0, 0, 0, 5],
                border: [false, false, false, false],
              },
              {
                text: "code",
                style: {
                  alignment: "center",
                  bold: true,
                },
                margin: [0, 0, 0, 5],
                border: [false, false, false, false],
              },
              {
                text: "รายการ",
                style: {
                  alignment: "center",
                  bold: true,
                },
                margin: [0, 0, 0, 5],
                border: [false, false, false, false],
              },
              {
                text: "จำนวน",
                style: {
                  alignment: "center",
                  bold: true,
                },
                margin: [0, 0, 0, 5],
                border: [false, false, false, false],
              },
              {
                text: "ราคารวม",
                style: {
                  alignment: "center",
                  bold: true,
                },
                margin: [0, 0, 0, 5],
                border: [false, false, false, false],
              },
              {
                text: "เบิกได้",
                style: {
                  alignment: "center",
                  bold: true,
                },
                margin: [0, 0, 0, 5],
                border: [false, false, false, false],
              },
              {
                text: "เบิกไม่ได้",
                style: {
                  alignment: "center",
                  bold: true,
                },
                margin: [0, 0, 0, 5],
                border: [false, false, false, false],
              },
            ],
            ...supplyData(),
          ],
        },
      },
    ],
    footer: {
      margin: [5, 0, 5, 0],
      table: {
        widths: ["*", 140, 70, 70, 70],
        body: [
          [
            {
              columns: [
                {
                  width: 130,
                  text: "กรุณาติดต่อหน่วยงานที่จ่าย",
                  bold: true,
                },
                {
                  width: "*",
                  text: props.supplyPerformDivision || "",
                },
              ],
              margin: [15, 0, 0, 0],
              border: [false, false, false, false],
            },

            {
              text: "รวม (บาท)",
              style: {
                bold: true,
              },
              margin: [0, 0, 0, 0],
              border: [false, false, false, false],
            },
            {
              text: formatComma(props.priceSummary?.total) || "0.00",
              style: {
                alignment: "center",
                bold: true,
              },
              margin: [0, 0, 0, 0],
              border: [false, false, false, false],
            },
            {
              text: formatComma(props.priceSummary?.claimable) || "0.00",
              style: {
                alignment: "center",
                bold: true,
              },
              margin: [0, 0, 0, 0],
              border: [false, false, false, false],
            },
            {
              text: formatComma(props.priceSummary?.nonClaimable) || "0.00",
              style: {
                alignment: "center",
                bold: true,
              },
              margin: [0, 0, 0, 0],
              border: [false, false, false, false],
            },
          ],
        ],
      },
    },
    images: {
      ...images,
    },
  };
};

export default FormSupplyReport;
